import React, { useState, useEffect } from 'react'
import { Global } from '@emotion/react'
import {
  ToastContext,
  Container,
  Image,
  TextCaption,
  TextBody,
  BlockingLoader,
  Button,
  Css,
  Tools,
  CarouselCards,
  HtmlContent
} from '@pdrj/pmi-iqos-ds-react'
import {
  CommonTranslations,
  PublishedComponent,
  parseTranslations,
  constants,
  cookie,
  JWTDecode,
  LayoutContainer,
  Assets
} from '@pdrj/pmi-iqos-shared-react'
import PointsRewardPopup from './points-popup'
import { getInitialPropsMock } from '@iqosphere-common/utils'
import {
  IqosphereApiManager,
  IqosphereAnalytics
} from '@iqosphere-common/services'

import ProfileAvatarTranslations from './profile-avatar.json'

const ProfileAvatarComponent = ({ translations, line, avatarPageData }) => {
  const [avatarList, setAvatarList] = useState([])
  const [avatarSelected, setAvatarSelected] = useState({})
  const [avatarAccessories, setAvatarAccessories] = useState([])
  const [selectedAccessory, setSelectedAccessory] = useState({})
  const [accessoryImageSelected, setAccessoryImageSelected] = useState(null)
  const [isDefaultAvatarSelected, setIsDefaultAvatarSelected] = useState(true)
  const [isPointsPopupOpen, setIsPointsPopupOpen] = useState(false)
  const [pointsAwarded, setPointsAwarded] = useState('')
  const [loading, setLoading] = useState(true)
  const [submittingForm, setIsSubmittingForm] = useState(false)
  const [hasUserSelected, setHasUserSelected] = useState(false)

  const api = new IqosphereApiManager(line)
  const popupToast = ToastContext.getContextActions().popupToast

  const contextValues = {
    page: 'page',
    profile: 'profile'
  }

  useEffect(() => {
    const loadData = async () => {
      await loadAvatarData()
    }
    loadData()
  }, [])

  const loadAvatarData = async () => {
    setLoading(true)
    const publishedAccessories = []
    const mappedAvatarList = []
    const removeAccessoryOption = {
      accessory_code: 'remove',
      baseImageSrc: Assets.urls.removeAccessoryIcon,
      name: translations.tk_removeAccessoryLabel
    }
    try {
      const res = await api.iqosphereProfile.getAvatarAccessories()
      res.accessories.forEach(accessory => {
        const unlockedAccessory = avatarPageData.accessories.find(
          publishedAccessory => publishedAccessory.accessory_code === accessory
        )
        if (unlockedAccessory) {
          publishedAccessories.push(unlockedAccessory)
        }
      })

      const publishedAvatars = avatarPageData.avatars.map(avatar => ({
        ...avatar,
        accessories: avatar.default
          ? [
              removeAccessoryOption,
              ...publishedAccessories.map(acc => ({
                ...acc,
                baseImageSrc: acc.base_image
              }))
            ]
          : [
              removeAccessoryOption,
              ...avatar.accessories
                .map(accessory => {
                  const baseImageSrc = publishedAccessories.find(
                    published =>
                      published.accessory_code === accessory.accessory_code
                  )
                  if (baseImageSrc) {
                    return {
                      ...accessory,
                      baseImageSrc: baseImageSrc.base_image,
                      name: baseImageSrc.name
                    }
                  }
                  return null
                })
                .filter(Boolean)
            ]
      }))
      const defaultAvatar = publishedAvatars.find(avatar => avatar.default)
      mappedAvatarList.push(defaultAvatar) // ensure default avatar is always first
      const nonDefaultAvatars = publishedAvatars.filter(
        avatar => !avatar.default
      )
      mappedAvatarList.push(...nonDefaultAvatars) // insert all avatars filtered by default value
      setAvatarList(mappedAvatarList)
      await loadUserData(mappedAvatarList)
    } catch (e) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_updateError
      })
    } finally {
      setLoading(false)
    }
  }

  const loadUserData = async mappedAvatarList => {
    let profileData = {}
    try {
      await api.iqosphereProfile.getAvatarProfile()
      const decodedToken = JWTDecode(
        cookie.get(constants.COOKIE_LOYALTY_AVATAR)
      )
      window.dispatchEvent(new window.CustomEvent('avatarCookieUpdated'))

      if (decodedToken && decodedToken.avt && decodedToken.avt.code) {
        profileData =
          mappedAvatarList.find(
            avatar => avatar.avatar_code === decodedToken.avt.code
          ) || {}
        const loadedAccessory =
          profileData?.accessories.find(
            acc => acc.accessory_code === decodedToken.avt.accessory
          ) || null
        setSelectedAccessory(loadedAccessory || {})
        setAccessoryImageSelected(loadedAccessory?.src || null)
      } else {
        profileData = mappedAvatarList.find(avatar => avatar.default) || {}
        setHasUserSelected(true)
      }
      setAvatarSelected(profileData)
      setAvatarAccessories(profileData.accessories)
      setIsDefaultAvatarSelected(profileData?.default || false)
    } catch (e) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_updateError
      })
    }
  }

  const onUpdateAvatar = async () => {
    setIsSubmittingForm(true)
    try {
      const res = await api.iqosphereProfile.updateAvatarProfile({
        avatar: avatarSelected.avatar_code,
        accessory: selectedAccessory?.accessory_code || ''
      })

      if (selectedAccessory.name) {
        IqosphereAnalytics.profileAvatarAnalytics({
          eventCategory:
            avatarPageData.context === 'page' ? 'Avatar' : 'My Account',
          eventLabel: `${avatarSelected.nickname} + ${selectedAccessory.name} - Save Avatar`
        })
      } else {
        IqosphereAnalytics.profileAvatarAnalytics({
          eventCategory:
            avatarPageData.context === 'page' ? 'Avatar' : 'My Account',
          eventLabel: `${avatarSelected.nickname} - Save Avatar`
        })
      }

      if (res && res.award && res.award.points) {
        setPointsAwarded(res.award.points.toString())
        setIsPointsPopupOpen(true)
      }

      setHasUserSelected(false)
      window.dispatchEvent(new window.CustomEvent('avatarCookieUpdated'))
    } catch (e) {
      popupToast({
        type: ToastContext.types.error,
        subtitle: translations.tk_updateError
      })
    } finally {
      setIsSubmittingForm(false)
    }
  }

  const onSelectAvatar = (data, isAvatar) => {
    if (submittingForm) return
    setHasUserSelected(true)
    if (isAvatar) {
      const selectedAvatar = avatarList.find(
        avatar => avatar.avatar_code === data.avatar_code
      )
      if (data.avatar_code === avatarSelected.avatar_code) return

      setSelectedAccessory({})
      setAvatarSelected(selectedAvatar)
      setAccessoryImageSelected(null)
      setAvatarAccessories(selectedAvatar.accessories)
      setIsDefaultAvatarSelected(selectedAvatar.default)
      return
    }

    // is an accessory
    const accessory =
      avatarSelected?.accessories.find(
        acc => acc.accessory_code === data.accessory_code
      ) || null
    const isRemoveAccessory = accessory?.accessory_code === 'remove'
    setSelectedAccessory(isRemoveAccessory ? {} : accessory)
    setAvatarSelected(avatarSelected)
    setAccessoryImageSelected(accessory?.src || null)
  }

  const renderAvatar = ({
    selectedImage,
    avatarImage,
    avatarNickname,
    avatarCode,
    isAccessory = false
  }) => {
    const imageSize = !isAccessory ? '20' : '16'
    const indicatorSize = !isAccessory ? '90px' : '74px'
    const topValue = !isAccessory ? '-4.5px' : '-5.5px'
    const isRemoveAccessory = avatarCode === 'remove'
    return (
      <Container
        position={Container.positions.relative}
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}
        alignItems={Container.alignItemsValues.center}>
        {selectedImage === avatarCode && !isRemoveAccessory && (
          <Css
            s={{
              position: 'absolute',
              height: indicatorSize,
              width: indicatorSize,
              border: '2px solid turquoise',
              borderRadius: '100%',
              top: topValue
            }}
          />
        )}
        <Container position={Container.positions.relative}>
          <Image
            src={avatarImage}
            width={imageSize}
            height={imageSize}
            rounded={Image.roundedSizes.full}
          />
          {isDefaultAvatarSelected && isAccessory && !isRemoveAccessory && (
            <Css
              s={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'grey',
                opacity: 0.5,
                borderRadius: '100%',
                zIndex: 1,
                pointerEvents: 'none'
              }}
            />
          )}
        </Container>

        <TextBody
          breakWords
          alignment={TextBody.alignments.center}
          size={TextBody.sizes.xs}
          additionalClasses="q-pt-2"
          darkMode={isPageContext()}>
          {avatarNickname}
        </TextBody>
      </Container>
    )
  }

  const reduceArray = (all, one, i, columns) => {
    const ch = Math.floor(i / columns)
    all[ch] = [].concat(all[ch] || [], one)
    return all
  }

  const avatarMobileCards = () => {
    const cards = avatarList
      .reduce((all, one, i) => reduceArray(all, one, i, 3), [])
      .map((items, i) => {
        return (
          <Container display={Container.displays.flex} key={i} fullWidth>
            {items.map((avatar, index) => {
              return (
                <Container
                  key={index}
                  onClickHandler={() => onSelectAvatar(avatar, true)}
                  pl={index === 0 ? '' : '3'}
                  pr={index === 2 ? '' : '3'}>
                  {renderAvatar({
                    selectedImage: avatarSelected.avatar_code,
                    avatarImage: avatar.base_avatar_src,
                    avatarNickname: avatar.nickname,
                    avatarCode: avatar.avatar_code,
                    indicatorCss: '-1px'
                  })}
                </Container>
              )
            })}
          </Container>
        )
      })
    return cards
  }

  const onPopupClose = () => {
    IqosphereAnalytics.profileAvatarAnalytics({
      eventCategory:
        avatarPageData.context === 'page' ? 'Avatar' : 'My Account',
      eventLabel: 'Avatar Reward - Close'
    })
    setIsPointsPopupOpen(false)
  }

  const accessoryMobileCards = () => {
    const cards = avatarAccessories
      .reduce((all, one, i) => reduceArray(all, one, i, 4), [])
      .map((items, i) => {
        return (
          <Css key={i} s={{ marginLeft: i > 0 && '-10px' }}>
            <Container display={Container.displays.flex}>
              {items.map((accessory, index) => {
                return (
                  <Container
                    key={index}
                    onClickHandler={
                      isDefaultAvatarSelected
                        ? null
                        : () => onSelectAvatar(accessory, false)
                    }
                    width="24">
                    {renderAvatar({
                      selectedImage: selectedAccessory.accessory_code,
                      avatarImage: accessory.baseImageSrc,
                      avatarCode: accessory.accessory_code,
                      avatarNickname: accessory.name,
                      isAccessory: true
                    })}
                  </Container>
                )
              })}
            </Container>
          </Css>
        )
      })
    return cards
  }

  const isPageContext = () => {
    const context = avatarPageData.context
    switch (context) {
      case contextValues.page:
        return true
      case contextValues.profile:
        return false
      default:
        return false
    }
  }

  return !avatarPageData.enabled ? (
    <HtmlContent content={avatarPageData.disabled_content} />
  ) : loading ? (
    <BlockingLoader loading />
  ) : (
    <Css
      s={{
        background: isPageContext()
          ? 'linear-gradient(180deg, rgba(0, 209, 210, 0.8) 0%, #000000 66.35%)'
          : '',
        position: 'relative',
        overflow: 'hidden'
      }}>
      <Container
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}
        alignItems={['', Container.alignItemsValues.center]}
        fullWidth
        fullHeight
        pt="10">
        <Container
          fullWidth
          flexShrink={2}
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          <Css s={{ filter: 'drop-shadow(0px 0px 30px rgba(0, 209, 210, 1))' }}>
            <Image
              src={
                accessoryImageSelected || avatarSelected.base_avatar_src || ''
              }
              width="40"
              height="40"
              rounded={Image.roundedSizes.full}
            />
          </Css>
          <Container pt="4" flexShrink={2} display={Container.displays.flex}>
            <TextBody
              size={TextBody.sizes.l}
              additionalClasses="q-pl-2"
              darkMode={isPageContext()}>
              {avatarSelected?.nickname || ''}
            </TextBody>
          </Container>
        </Container>

        <Container
          px="10"
          py="4"
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={
            Tools.isDesktop()
              ? Container.alignItemsValues.center
              : Container.alignItemsValues.start
          }>
          <TextCaption darkMode={isPageContext()}>
            {translations.tk_description}
          </TextCaption>
          <TextBody darkMode={isPageContext()} breakWords>
            {translations.tk_descriptionSubtext}
          </TextBody>
        </Container>

        {/* Avatar Selection */}
        {Tools.isDesktop() ? (
          <Container display={Container.displays.flex}>
            {avatarList.map((avatar, index) => (
              <Container
                pt="2"
                pl="2"
                key={index}
                fullWidth
                onClickHandler={() => onSelectAvatar(avatar, true)}>
                {renderAvatar({
                  selectedImage: avatarSelected.avatar_code,
                  avatarImage: avatar.base_avatar_src,
                  avatarNickname: avatar.nickname,
                  avatarCode: avatar.avatar_code
                })}
              </Container>
            ))}
          </Container>
        ) : (
          <LayoutContainer xMainLayout fullWidth>
            <Global
              styles={`
                @media (min-width: 390px) {
                  .avatar-carousel-mobile {
                    width: 95% !important;
                  }
                }
                @media (min-width: 420px) {
                  .avatar-carousel-mobile {
                    width: 85% !important;
                  }
                }
              `}
            />
            <Container additionalClasses="avatar-carousel-mobile">
              <CarouselCards
                cards={avatarMobileCards()}
                imageSlideSize="auto"
              />
            </Container>
          </LayoutContainer>
        )}

        {/* Accessory selection */}
        {avatarAccessories.length > 1 && (
          <Container pt={['', '6']}>
            <Container
              px="10"
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.column}
              alignItems={
                Tools.isDesktop()
                  ? Container.alignItemsValues.center
                  : Container.alignItemsValues.start
              }>
              <TextCaption darkMode={isPageContext()}>
                {translations.tk_myAccessories}
              </TextCaption>
              <TextBody additionalClasses="q-pb-4" darkMode={isPageContext()}>
                {translations.tk_myAccessoriesSubtext}
              </TextBody>
            </Container>
            {Tools.isDesktop() ? (
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}>
                {avatarAccessories.map((accessory, index) => (
                  <Container
                    key={index}
                    pt="2"
                    px="4"
                    flexDirection={Container.flexDirections.column}
                    onClickHandler={
                      (accessory.accessory_code === 'remove' &&
                        isDefaultAvatarSelected) ||
                      !isDefaultAvatarSelected
                        ? () => onSelectAvatar(accessory, false)
                        : null
                    }>
                    {renderAvatar({
                      selectedImage: selectedAccessory.accessory_code,
                      avatarImage: accessory.baseImageSrc,
                      avatarCode: accessory.accessory_code,
                      avatarNickname: accessory.name,
                      isAccessory: true
                    })}
                  </Container>
                ))}
              </Container>
            ) : (
              <LayoutContainer xMainLayout>
                <CarouselCards
                  cards={accessoryMobileCards()}
                  imageSlideSize="auto"
                />
              </LayoutContainer>
            )}
          </Container>
        )}

        <Container
          pt="8"
          pb="10"
          fullWidth
          display={Container.displays.flex}
          flexShrink={2}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          <Button
            variant={Button.variants.primary}
            onClickHandler={onUpdateAvatar}
            text={
              hasUserSelected
                ? translations.tk_enabledSaveButton
                : translations.tk_disabledSaveButton
            }
            disabled={
              !avatarSelected.avatar_code || submittingForm || !hasUserSelected
            }
            loading={submittingForm}
            darkMode={isPageContext()}
          />
          <Container pt="10">
            <HtmlContent
              content={
                isPageContext()
                  ? avatarPageData.page_bottom
                  : avatarPageData.account_bottom
              }
            />
          </Container>
        </Container>
      </Container>
      <PointsRewardPopup
        isOpen={isPointsPopupOpen}
        onClose={onPopupClose}
        pointsAwarded={pointsAwarded}
        htmlContent={avatarPageData.reward}
        translations={translations}
      />
    </Css>
  )
}

const ProfileAvatar = props => {
  return (
    <ToastContext>
      <PublishedComponent
        fullWidth
        innerComponent={ProfileAvatarComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfileAvatarTranslations,
  ...CommonTranslations
}

const defaultTranslations = {
  ...parseTranslations(ComponentTranslations, true)
}

const ComponentDefaultProps = {
  componentName: 'ProfileAvatar',
  translations: defaultTranslations,
  defaultTranslations: defaultTranslations,
  translationsConfig: ComponentTranslations
}

ProfileAvatar.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: () => {
      const avatarPageData =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('avatar')
          : (req.body.extra && req.body.extra.avatar) || []
      return { avatarPageData }
    }
  })
}

export default ProfileAvatar
